<script lang="ts">
	import { onMount, untrack } from 'svelte';
	import { Icon, IconButton } from '@andresot/ui';
	import { music, musicTime } from '$stores/music.svelte';
	import { session } from '$stores/session.svelte';
	import { app } from '$stores/app.svelte';
	import { mediaFocus } from '$stores/media-focus.svelte';
	import Modal from '../Modal.svelte';
	import MusicTrackList from './MusicTrackList.svelte';
	let audio: HTMLAudioElement;
	type MusicAction = Parameters<typeof navigator.mediaSession.setActionHandler>[0];
	type MusicHandler = NonNullable<Parameters<typeof navigator.mediaSession.setActionHandler>[1]>;
	$effect(() => {
		const id = session.phase === 'ready' ? (session.user?.id ?? null) : null;
		untrack(() => music.account(id));
	});
	$effect(() => {
		const blocked = !!app.activeCall || mediaFocus.blockers.length > 0;
		music.blocked = blocked;
		if (blocked) untrack(() => music.pause());
	});
	onMount(() => {
		music.attach(audio);
		const interrupt = (event: Event) => {
			if (event.target instanceof HTMLMediaElement && event.target !== audio) music.pause();
		};
		const save = () => music.persist();
		document.addEventListener('play', interrupt, true);
		window.addEventListener('pagehide', save);
		const handlers: Partial<Record<MusicAction, MusicHandler>> = {
			play: () => void music.play(),
			pause: () => music.pause(),
			previoustrack: () => music.previous(),
			nexttrack: () => void music.next(),
			seekto: (details) => {
				if (details.seekTime !== undefined) music.seek(details.seekTime);
			},
			seekbackward: () => music.seek(music.position - 10),
			seekforward: () => music.seek(music.position + 10)
		};
		for (const [name, handler] of Object.entries(handlers)) {
			try {
				navigator.mediaSession?.setActionHandler(name as MusicAction, handler!);
			} catch {
				/* Действие не поддерживается браузером. */
			}
		}
		return () => {
			music.detach();
			document.removeEventListener('play', interrupt, true);
			window.removeEventListener('pagehide', save);
			for (const name of Object.keys(handlers)) {
				try {
					navigator.mediaSession?.setActionHandler(name as MusicAction, null);
				} catch {
					/* Не поддерживается. */
				}
			}
		};
	});
</script>

<audio
	data-music-player
	bind:this={audio}
	preload="none"
	onloadedmetadata={() => music.ready()}
	ontimeupdate={() => music.tick()}
	onpause={() => (music.playing = false)}
	onended={() => void music.next(true)}
	onerror={() => {
		if (!music.loading && music.current && audio.src) void music.failed();
	}}
></audio>
{#if music.current && session.phase === 'ready'}
	<aside class="music-dock" aria-label="Музыкальный плеер">
		<button
			class="now"
			onclick={() => (music.expanded = true)}
			aria-label="Открыть музыкальный плеер"
		>
			{#if music.current.artwork}<img
					src={music.current.artwork}
					alt=""
					referrerpolicy="no-referrer"
				/>{:else}<span class="placeholder"><Icon name="music" size={24} /></span>{/if}
			<span
				><strong>{music.current.title}</strong><small
					>{music.error
						? 'Не удалось воспроизвести · Подробнее'
						: music.loading
							? 'Подключаемся…'
							: music.current.artist}</small
				></span
			>
		</button>
		<IconButton
			icon="heart"
			label={music.current.liked ? 'Убрать из избранного' : 'Мне нравится'}
			onclick={() => music.current && void music.like(music.current)}
		/>
		<IconButton
			icon={music.playing ? 'pause' : 'play'}
			label={music.playing ? 'Пауза музыки' : 'Продолжить музыку'}
			disabled={music.blocked}
			onclick={() => music.toggle()}
		/>
		<IconButton
			icon="chevron-right"
			label="Следующий трек"
			disabled={music.blocked}
			onclick={() => void music.next()}
		/>
		<IconButton icon="close" label="Закрыть музыкальный плеер" onclick={() => music.close()} />
		<span
			class="progress"
			style:width={`${music.duration ? (music.position / music.duration) * 100 : 0}%`}
		></span>
	</aside>
{/if}
{#if music.expanded && music.current && session.phase === 'ready'}
	<Modal title="Сейчас играет" cancelLabel="Свернуть" oncancel={() => (music.expanded = false)}>
		<div class="full-player">
			{#if music.current.artwork}<img
					class="cover"
					src={music.current.artwork}
					alt={`Обложка ${music.current.title}`}
					referrerpolicy="no-referrer"
				/>{:else}<div class="cover fallback"><Icon name="music" size={64} /></div>{/if}
			<h2>{music.current.title}</h2>
			<p>{music.current.artist}</p>
			{#if !music.current.available}<p role="status">
					Этот трек сейчас недоступен у источника.
				</p>{/if}
			{#if music.current.licenseUrl}
				<p class="attribution">
					{music.current.attribution}
					<a href={music.current.externalUrl} target="_blank" rel="noreferrer">Оригинал</a> ·
					<a href={music.current.licenseUrl} target="_blank" rel="noreferrer"
						>Лицензия Creative Commons</a
					>
				</p>
			{/if}
			<input
				type="range"
				aria-label="Позиция воспроизведения"
				min="0"
				max={music.duration || music.current.duration}
				step="1"
				value={music.position}
				oninput={(event) => music.seek(Number(event.currentTarget.value))}
			/>
			<div class="times">
				<span>{musicTime(music.position)}</span><span
					>{musicTime(music.duration || music.current.duration)}</span
				>
			</div>
			<div class="controls">
				<IconButton
					icon="chevron-left"
					label="Предыдущий трек"
					onclick={() => music.previous()}
				/><button
					class="play"
					disabled={music.blocked}
					aria-label={music.playing ? 'Пауза' : 'Воспроизвести'}
					onclick={() => music.toggle()}
					><Icon name={music.playing ? 'pause' : 'play'} size={32} /></button
				><IconButton
					icon="chevron-right"
					label="Следующий трек"
					disabled={music.blocked}
					onclick={() => void music.next()}
				/>
			</div>
			<div class="options">
				<button
					aria-pressed={music.shuffle}
					onclick={() => {
						music.shuffle = !music.shuffle;
						music.persist();
					}}>Перемешать</button
				><button
					onclick={() => {
						music.repeat = music.repeat === 'off' ? 'all' : music.repeat === 'all' ? 'one' : 'off';
						music.persist();
					}}
					>Повтор: {music.repeat === 'off'
						? 'выкл.'
						: music.repeat === 'all'
							? 'всё'
							: 'один'}</button
				><button onclick={() => music.current && void music.like(music.current)}
					>{music.current.liked ? '♥ В избранном' : '♡ Мне нравится'}</button
				><button onclick={() => music.current && void music.share(music.current)}>Поделиться</button
				>
			</div>
			{#if music.blocked}<p role="status">
					Музыка на паузе, пока используется звонок, запись или видео.
				</p>{/if}
			{#if music.error}<div role="alert">
					<p>{music.error}</p>
					<button onclick={() => music.toggle()} disabled={music.blocked}
						>Повторить подключение</button
					>
				</div>{/if}
			<h3>Очередь · {music.queue.length}</h3>
			<MusicTrackList tracks={music.queue} />
		</div>
	</Modal>
{/if}

<style>
	.music-dock {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: var(--z-dropdown);
		box-sizing: border-box;
		height: calc(var(--music-dock-height) + var(--safe-bottom));
		padding: var(--size-8) var(--size-12) calc(var(--size-8) + var(--safe-bottom));
		display: flex;
		align-items: center;
		gap: var(--size-4);
		background: var(--float-glass-flat);
		border-top: 1px solid var(--border-subtle);
		color: var(--text-primary);
	}
	.now {
		flex: 1;
		min-width: 0;
		display: flex;
		align-items: center;
		gap: var(--size-12);
		text-align: left;
		padding: 0;
		border: 0;
		background: none;
		color: inherit;
		cursor: pointer;
	}
	.now img,
	.placeholder {
		width: var(--size-44);
		height: var(--size-44);
		border-radius: var(--radius-s);
		object-fit: cover;
		flex-shrink: 0;
	}
	.placeholder {
		display: grid;
		place-items: center;
		background: var(--background-secondary);
	}
	.now span {
		display: grid;
		min-width: 0;
		gap: var(--size-4);
	}
	.now strong,
	.now small {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
	.now small {
		color: var(--text-secondary);
	}
	.progress {
		position: absolute;
		left: 0;
		top: 0;
		height: var(--size-2);
		background: var(--accent-primary);
		pointer-events: none;
	}
	.full-player {
		text-align: center;
		max-height: 65dvh;
		overflow-y: auto;
	}
	.cover {
		width: min(100%, var(--music-cover-size));
		aspect-ratio: 1;
		object-fit: cover;
		border-radius: var(--radius-l);
		margin: 0 auto;
	}
	.fallback {
		display: grid;
		place-items: center;
		background: linear-gradient(135deg, var(--service-purple), var(--service-cyan));
	}
	h2 {
		overflow-wrap: anywhere;
		font-size: var(--font-size-2xl);
		margin-bottom: var(--size-4);
	}
	p {
		color: var(--text-secondary);
		overflow-wrap: anywhere;
	}
	.attribution {
		font-size: var(--font-size-s);
	}
	input {
		width: 100%;
		accent-color: var(--accent-primary);
		cursor: pointer;
	}
	.times {
		display: flex;
		justify-content: space-between;
		color: var(--text-secondary);
		font-size: var(--font-size-s);
	}
	.controls {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--size-24);
		margin-block: var(--size-16);
	}
	button {
		cursor: pointer;
		font: inherit;
		color: inherit;
	}
	.play {
		display: grid;
		place-items: center;
		width: var(--size-64);
		height: var(--size-64);
		border-radius: 50%;
		background: var(--accent-primary);
		color: var(--text-on-accent);
		border: 0;
	}
	button:disabled {
		opacity: 0.5;
		cursor: default;
	}
	.options {
		display: flex;
		gap: var(--size-8);
		flex-wrap: wrap;
		justify-content: center;
	}
	.options button,
	[role='alert'] button {
		padding: var(--size-8) var(--size-12);
		background: var(--background-secondary);
		border: 1px solid var(--border-subtle);
		border-radius: var(--radius-m);
	}
	.options [aria-pressed='true'] {
		border-color: var(--accent-primary);
	}
</style>
